import { useEffect, useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { consumeOrderLookupReturnContext } from '../utils/orderLookupReturn';
import {
  clearRouteScrollPosition,
  consumeRouteScrollPosition,
} from '../utils/routeScrollMemory';

const scrollNodeToTop = (node) => {
  if (!node) return;

  if (typeof node.scrollTo === 'function') {
    try {
      node.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (error) {
      try {
        node.scrollTo(0, 0);
      } catch (legacyError) {
        void error;
        void legacyError;
      }
    }
  }

  if ('scrollTop' in node) {
    try {
      node.scrollTop = 0;
    } catch (error) {
      void error;
    }
  }

  if ('scrollLeft' in node) {
    try {
      node.scrollLeft = 0;
    } catch (error) {
      void error;
    }
  }
};

const restoreRouteScrollPosition = (scrollTop) => {
  if (typeof document === 'undefined') return false;

  const node = document.querySelector('[data-route-scroll-container="true"]')
    || document.querySelector('.main-scrollable')
    || document.scrollingElement
    || document.documentElement
    || document.body;

  if (!node) return false;

  if (typeof node.scrollTo === 'function') {
    try {
      node.scrollTo({ top: scrollTop, left: 0, behavior: 'auto' });
    } catch (error) {
      void error;
    }
  }

  try {
    node.scrollTop = scrollTop;
  } catch (error) {
    void error;
  }

  const maxScrollTop = Math.max(0, Number(node.scrollHeight || 0) - Number(node.clientHeight || 0));
  const targetIsReachable = maxScrollTop >= scrollTop - 2;
  return targetIsReachable && Math.abs(Number(node.scrollTop || 0) - scrollTop) <= 2;
};

export const resetRouteScrollPosition = () => {
  if (typeof document === 'undefined') return;

  const scrollCandidates = [
    document.querySelector('[data-route-scroll-container="true"]'),
    document.querySelector('.main-scrollable'),
    document.scrollingElement,
    document.documentElement,
    document.body,
    typeof window !== 'undefined' ? window : null
  ].filter(Boolean);

  const handledNodes = new Set();
  scrollCandidates.forEach((node) => {
    if (handledNodes.has(node)) return;
    handledNodes.add(node);
    scrollNodeToTop(node);
  });
};

function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return undefined;

    const previousValue = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = previousValue;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const lookupReturnContext = consumeOrderLookupReturnContext(location, navigationType);
    if (lookupReturnContext) {
      clearRouteScrollPosition(location);
    }
    const returnContext = lookupReturnContext || consumeRouteScrollPosition(location, navigationType);
    if (returnContext) {
      let restored = restoreRouteScrollPosition(returnContext.scrollTop);

      const restoreTimeouts = [120, 400, 900, 1600, 2600, 4200, 6500, 9000].map((delay) => (
        window.setTimeout(() => {
          if (!restored) {
            restored = restoreRouteScrollPosition(returnContext.scrollTop);
          }
        }, delay)
      ));

      return () => {
        restoreTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
      };
    }

    clearRouteScrollPosition(location);
    resetRouteScrollPosition();

    const rafId = window.requestAnimationFrame(() => {
      resetRouteScrollPosition();
    });
    const timeoutId = window.setTimeout(() => {
      resetRouteScrollPosition();
    }, 120);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [location, navigationType]);

  return null;
}

export default ScrollToTop;
